import styled, { css } from 'styled-components'
import { colors, fontScope, radius, visuallyHidden } from '../tokens'

export const Wrapper = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

export const ToggleAll = styled.button`
  align-self: flex-end;
  padding: 4px 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  color: ${colors.primaryStrong};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  @media print {
    display: none;
  }
`

export const Frame = styled.div`
  border: 1px solid ${colors.tableBorder};
  border-radius: ${radius.table};
  background: ${colors.white};
  overflow: hidden;
  overflow: clip;

  @media print {
    overflow: visible;
    border-radius: 0;
  }
`

export const Table = styled.div<{ $minWidth: number }>`
  min-width: ${({ $minWidth }) => $minWidth}px;

  @media print {
    min-width: 0;

    & * {
      font-size: 9px !important;
      line-height: 1.3 !important;
    }
  }
`

const grid = css<{ $template: string }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: stretch;

  @media print {
    break-inside: avoid;
  }
`

export const HeaderRow = styled.div<{ $template: string }>`
  ${grid}
  background: ${colors.zebra};
  border-bottom: 1px solid ${colors.tableBorder};
`

export const Cell = styled.div<{ $align?: 'left' | 'right', $strong?: boolean, $sticky?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  gap: 8px;
  min-width: 0;
  padding: 14px 10px;
  font-size: 14px;
  font-weight: ${({ $strong }) => ($strong ? 700 : 500)};
  line-height: 20px;
  color: ${colors.ink800};
  text-align: ${({ $align }) => $align ?? 'left'};
  white-space: nowrap;
  ${({ $sticky }) =>
    $sticky === true &&
    css`
      position: sticky;
      left: 0;
      z-index: 2;
      background: inherit;
      box-shadow: inset -1px 0 0 ${colors.tableBorder};
    `}

  @media print {
    padding: 3px 4px;
    position: static;
    box-shadow: none;
  }
`

export const SortButton = styled.button<{ $align?: 'left' | 'right' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 11px;
  font-weight: 700;
  line-height: 16.5px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${colors.ink500};
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

export const SortMark = styled.span`
  font-size: 10px;
  color: ${colors.ink400};
`

export const Row = styled.div<{ $template: string, $open: boolean, $static?: boolean }>`
  ${grid}
  min-height: 56px;
  border-top: 1px solid ${colors.rowDivider};
  background: ${({ $open }) => ($open ? colors.accent : colors.white)};
  cursor: ${({ $static }) => ($static === true ? 'default' : 'pointer')};

  &:hover {
    background: ${colors.accent};
  }
`

export const StaticLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${colors.ink950};
`

export const RowToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  color: ${colors.ink950};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

export const Chevron = styled.img<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform 0.15s ease;

  @media print {
    display: none;
  }
`

export const Label = styled.span`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow-wrap: anywhere;
  white-space: normal;
  line-height: 18px;
`

export const Muted = styled.span`
  color: ${colors.ink500};
`

export const Hidden = styled.span`
  ${visuallyHidden}
`

export const Low = styled.span`
  font-weight: 700;
  color: ${colors.dangerText};
`

export const Occupancy = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

export const OccupancyText = styled.span`
  flex-shrink: 0;
  min-width: 36px;
  font-weight: 700;
  text-align: right;
`

export const Detail = styled.div`
  grid-column: 1 / -1;
  padding: 12px 8px 16px 12px;
  border-top: 1px solid ${colors.rowDivider};
  background: ${colors.white};

  &[hidden] {
    display: none;
  }

  @media print {
    padding: 4px 0 8px 12px;
  }
`

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  &[hidden] {
    display: none;
  }
`

export const TotalRow = styled.div<{ $template: string }>`
  ${grid}
  background: ${colors.totalBar};

  & * {
    color: ${colors.white};
    font-weight: 700;
  }
`
